import React, { useCallback, useState, useEffect } from "react";
import { Form, Image, Message, Button } from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { User, Role, Company, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { ENV } from "../../../../utils";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { initialValues, validationSchema } from "./UserForm.form";
import "./UserForm.scss";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PeerAccessFields, initialPeerAccess, saveUserPeerAccess } from "./PeerAccessFields";

const userController = new User();
const roleController = new Role();
const companyController = new Company();
const siteController = new Site();

export function UserForm(props) {
  const { close, onReload, user } = props;
  const {
    accessToken,
    user: { _id: loggedInUserId, role, company },
  } = useAuth();
  const [listRoles, setListRoles] = useState([]);
  const [listCompanies, setListCompanies] = useState([]);
  const [listSites, setListSites] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [error, setError] = useState("");
  // Acceso a la otra app Sustemia (lo deciden admin o master)
  const [peerAccessInitial] = useState(() => initialPeerAccess(user));
  const [peerAccess, setPeerAccess] = useState(peerAccessInitial);

  const { translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  useEffect(() => {
    roleController.getRoles(accessToken, true).then((response) => {
      setListRoles(response);
    });
  }, []);

  useEffect(() => {
    if (isMaster(role)) {
      // Al editar, traer todas las empresas (incluso inactivas) para que aparezca
      // la empresa del usuario aunque esté inactiva. Al crear, solo activas.
      const activeFilter = user ? undefined : true;
      companyController.getCompanies(accessToken, activeFilter).then((response) => {
        setListCompanies(response);
      });
    } else if (isAdmin(role) && company) {

      siteController
        .getSitesByCompany(accessToken, company, true)
        .then((response) => {
          setListSites(response);
        });
        companyController
        .getCompany(accessToken, company)
        .then((response) => {
          setCompanyData(response);
        });
    }
  }, []);

  const formik = useFormik({
    initialValues: initialValues(user),
    validationSchema: validationSchema(user, role),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (peerAccess.enabled && !peerAccess.roleName) {
        setError(t("peer_role_required"));
        return;
      }
      try {
        let peerWarning = null;
        if (!user) {
          if (isAdmin(role)) {
            formValue.company = companyData._id;
          }
          // Llamamos a la función para eliminar los campos con valores vacíos
          const cleanedData = removeNullFields(formValue);
          const response = await userController.createUser(
            accessToken,
            cleanedData
          );
          if (response.code && response.code === 500 && response.code === 400) {
               toast.error("Error al crear el usuario!");
          }else {
               toast.success("Usuario creado correctamente!");
               peerWarning = await saveUserPeerAccess(accessToken, {
                 email: cleanedData.email, initial: peerAccessInitial, value: peerAccess,
               });
          }
        } else {
          if (isAdmin(role) && !user.company) {
            formValue.company = companyData._id;
          }
          const newData = removeNullFields(formValue);

          let response1=await userController.updateUser(accessToken, user._id, newData);
          if(response1.code && response1.code ===200){
            toast.success("Usuario actualizado correctamente!");
            peerWarning = await saveUserPeerAccess(accessToken, {
              user, initial: peerAccessInitial, value: peerAccess,
            });
          }

        }
        if (peerWarning) window.alert(peerWarning);
         // Espera 120 ms antes de cerrar el modal o hacer un reload
         setTimeout(() => {
          onReload(true);
          close();
        }, 1200);
      } catch (error) {
        setError(error.msg);
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (isMaster(role) && formik.values.company) {
      siteController
        .getSitesByCompany(accessToken, formik.values.company, true)
        .then((response) => {
          setListSites(response);
        });
    }
  }, [formik.values.company]);

  // Determina si el rol seleccionado en el form es administrador/admin
  const selectedRole = listRoles.find((r) => r._id === formik.values.role);
  const selectedRoleIsAdmin =
    selectedRole?.name?.toLowerCase() === "admin" ||
    selectedRole?.name?.toLowerCase() === "administrador";

  const isSelfEdit = !!user && !!loggedInUserId && String(user._id) === String(loggedInUserId);

  // El rol de una cuenta master nunca se puede tocar — ni editándola otro
  // master, ni editando su propio perfil — un master siempre es master.
  const targetIsMaster = isMaster(user?.role);

  // Empresa/Sitio de una cuenta master tampoco se pueden tocar, salvo que
  // sea el propio master editando su propio perfil. Nombre, apellido,
  // sector y posición sí siguen siendo editables siempre, son solo datos
  // descriptivos sin implicancia de privilegios.
  const hideCompanySite = targetIsMaster && !isSelfEdit;

  // Un admin (no master) editando su PROPIO perfil tampoco puede cambiar
  // su propio rol (evita que se autodegrade/escale sin querer).
  const lockOwnRole = isSelfEdit && !isMaster(role);

  // Al cambiar a rol admin, limpiar el sitio
  useEffect(() => {
    if (selectedRoleIsAdmin) {
      formik.setFieldValue("sites", []);
    }
  }, [selectedRoleIsAdmin]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      formik.setFieldValue("avatar", URL.createObjectURL(file));
      formik.setFieldValue("fileAvatar", file);
    },
    [formik]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg"], "image/png": [".png"] },
    onDrop,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return `${ENV.BASE_PATH}/${formik.values.avatar}`;
    }
    return image.noAvatar;
  };

  return (
    <Form className="user-form" onSubmit={formik.handleSubmit}>
      {error.length > 0 ? (
        <Message visible={error.length > 0} negative>
          {error}
        </Message>
      ) : null}

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("name")}{!isMaster(role) && !isAdmin(role) && <span style={{ color: "#C0392B" }}> *</span>}</label>}
          name="firstname"
          onChange={formik.handleChange}
          value={formik.values.firstname}
          error={formik.errors.firstname}
        />
        <Form.Input
          label={<label>{t("lastname")}{!isMaster(role) && !isAdmin(role) && <span style={{ color: "#C0392B" }}> *</span>}</label>}
          name="lastname"
          onChange={formik.handleChange}
          value={formik.values.lastname}
          error={formik.errors.lastname}
        />
      </Form.Group>

      {!hideCompanySite && (
      <Form.Group widths="equal">
        {isMaster(role) ? (
          <>
            <Form.Dropdown
              label={t("company")}
              placeholder={t("select")}
              options={listCompanies.map((ds) => ({
                key: ds._id,
                text: ds.name,
                value: ds._id,
              }))}
              selection
              onChange={(_, data) => formik.setFieldValue("company", data.value)}
              value={formik.values.company}
              error={formik.errors.company}
            />
            {!selectedRoleIsAdmin && (
              <Form.Dropdown
                label={t("site")}
                placeholder={t("select")}
                options={listSites.map((ds) => ({
                  key: ds._id,
                  text: ds.name,
                  value: ds._id,
                }))}
                selection
                multiple
                onChange={(_, data) => formik.setFieldValue("sites", data.value)}
                value={formik.values.sites || []}
                error={formik.errors.sites}
              />
            )}
          </>
        ) : isAdmin(role) && company ? (
          <>
            <Form.Input
              label={t("company")}
              name="company"
              onChange={formik.handleChange}
              value={companyData ? companyData.name : ""}
              error={formik.errors.company}
            />
            <Form.Dropdown
              label={t("site")}
              placeholder={t("select")}
              options={listSites.map((ds) => ({
                key: ds._id,
                text: ds.name,
                value: ds._id,
              }))}
              selection
              multiple
              onChange={(_, data) => formik.setFieldValue("sites", data.value)}
              value={formik.values.sites || []}
              error={formik.errors.sites}
            />
          </>
        ) : null}
      </Form.Group>
      )}

      <Form.Group widths="equal">
        <Form.Input
          label={t("sector")}
          name="sector"
          onChange={formik.handleChange}
          value={formik.values.sector}
          error={formik.errors.sector}
        />
        <Form.Input
          label={t("position")}
          name="position"
          onChange={formik.handleChange}
          value={formik.values.position}
          error={formik.errors.position}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("email")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="email"
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
        {isMaster(role) || isAdmin(role) ? (
          <Form.Dropdown
            label={<label>{t("role")} <span style={{ color: "#C0392B" }}>*</span></label>}
            placeholder={t("select")}
            options={listRoles.map((ds) => ({
              key: ds._id,
              text: ds.name,
              value: ds._id,
            }))}
            selection
            disabled={targetIsMaster || lockOwnRole}
            onChange={(_, data) => formik.setFieldValue("role", data.value)}
            value={formik.values.role}
            error={formik.errors.role}
          />
        ) : null}
      </Form.Group>

      {(isMaster(role) || isAdmin(role)) && !targetIsMaster &&
        !isMaster(listRoles.find((r) => r._id === formik.values.role)) && (
          <PeerAccessFields
            accessToken={accessToken}
            companyId={formik.values.company || user?.company?._id || user?.company || company?._id || company}
            value={peerAccess}
            onChange={setPeerAccess}
            t={t}
          />
        )}

      <Form.Input
        label={<label>{t("password")}{!user && <span style={{ color: "#C0392B" }}> *</span>}</label>}
        type="password"
        name="password"
        onChange={formik.handleChange}
        value={formik.values.password}
        error={formik.errors.password}
      />

      <div className="user-form__actions">
        <button type="submit" disabled={formik.isSubmitting} className="user-form__btn user-form__btn--primary">
          {formik.isSubmitting ? "…" : (!user ? t("save") : t("update"))}
        </button>
        <button type="button" onClick={close} className="user-form__btn user-form__btn--cancel">
          {t("cancel")}
        </button>
      </div>

      
               {/* Este es el contenedor que maneja las tostadas */}
                  <ToastContainer
                    position="top-center"
                    autoClose={1200}
                    hideProgressBar
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />

    </Form>
  );
}

function removeNullFields(obj) {
  // Recorre las propiedades del objeto
  for (const key in obj) {
    // Verifica si el campo es null
    if (obj[key] === null) {
      // Elimina el campo del objeto
      delete obj[key];
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      // Si el valor es un objeto, llama a la función recursivamente
      removeNullFields(obj[key]);
    }
  }
  return obj;
}
